
import { useState } from 'react';

interface HeaderProps {
  isScrolled: boolean;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

export default function Header({ isScrolled, isDarkMode, toggleDarkMode }: HeaderProps) {
  const [language, setLanguage] = useState('zh-TW');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { name: '首頁', href: '/' },
    { name: '賽事活動', href: '/events' },
    { name: '相簿', href: '#gallery' },
    { name: '最新消息', href: '#news' }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('搜尋:', searchQuery);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'zh-TW' ? 'en' : 'zh-TW');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? isDarkMode 
            ? 'bg-gray-900 shadow-lg' 
            : 'bg-white shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center space-x-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/ceffbd0e215ee7f6434042c96230b75d/9feb5678dc2baad9b9bcaf8a5890380d.jpeg"
              alt="大灣乒乓聯合會 Logo"
              className="h-12 w-12 mr-3 object-contain rounded-full"
            />
            <div className="flex flex-col">
              <span className={`text-xl font-bold transition-colors ${
                isScrolled 
                  ? isDarkMode ? 'text-white' : 'text-gray-900'
                  : 'text-white'
              }`}>
                大灣乒乓聯合會
              </span>
              <span className={`text-xs transition-colors ${
                isScrolled 
                  ? isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  : 'text-white/90'
              }`}>
                Greater Bay Table Tennis Association
              </span>
              <span className={`text-xs transition-colors ${
                isScrolled 
                  ? isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  : 'text-white/90'
              }`}>
                長城乒乓球會
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? isDarkMode
                      ? 'text-gray-200 hover:bg-gray-800 hover:text-emerald-400'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-emerald-600'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                {item.name}
              </a>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center space-x-3">
            {/* Search */}
            <div className="relative hidden md:block">
              {searchOpen ? (
                <form onSubmit={handleSearch} className="flex items-center">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜尋..."
                    className={`w-48 px-4 py-2 pr-10 rounded-lg border focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setSearchOpen(false)}
                    className={`absolute right-2 hover:text-gray-600 cursor-pointer ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-400'
                    }`}
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                    isScrolled
                      ? isDarkMode
                        ? 'text-gray-200 hover:bg-gray-800'
                        : 'text-gray-700 hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  <i className="ri-search-line text-xl"></i>
                </button>
              )}
            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? isDarkMode
                    ? 'text-gray-200 hover:bg-gray-800'
                    : 'text-gray-700 hover:bg-gray-100'
                  : 'text-white hover:bg-white/10'
              }`}
              title={isDarkMode ? '切換到淺色模式' : '切換到深色模式'}
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl`}></i>
            </button>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? isDarkMode
                    ? 'text-gray-200 hover:bg-gray-800'
                    : 'text-gray-700 hover:bg-gray-100'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              {language === 'zh-TW' ? 'EN' : '中文'}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? isDarkMode
                    ? 'text-gray-200 hover:bg-gray-800'
                    : 'text-gray-700 hover:bg-gray-100'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className={`lg:hidden border-t shadow-lg ${
          isDarkMode 
            ? 'bg-gray-900 border-gray-800' 
            : 'bg-white border-gray-200'
        }`}>
          <nav className="px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg font-medium cursor-pointer ${
                  isDarkMode
                    ? 'text-gray-200 hover:bg-gray-800'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {item.name}
              </a>
            ))}
            <div className={`pt-2 border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜尋..."
                  className={`flex-1 px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </form>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
